import { useRef } from 'react';
import { Box, Popover, TextField, Switch, FormControlLabel, InputAdornment } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useToggle } from 'src/hooks/useToggle';

type Props = {
  yAxisAuto: boolean;
  yAxisFrom: number | null;
  yAxisTo: number | null;
  onChange: (patch: {
    yAxisAuto?: boolean;
    yAxisFrom?: number | null;
    yAxisTo?: number | null;
  }) => void;
};

export const YAxisRangePicker = ({ yAxisAuto, yAxisFrom, yAxisTo, onChange }: Props) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const { state: isOpen, onToggle } = useToggle();

  const rangeError = !yAxisAuto && yAxisFrom !== null && yAxisTo !== null && yAxisFrom >= yAxisTo;

  const displayValue = yAxisAuto
    ? 'Auto'
    : yAxisFrom !== null && yAxisTo !== null
    ? `${yAxisFrom} – ${yAxisTo}`
    : '';

  return (
    <Box sx={{ display: 'flex', flex: '0 0 130px' }}>
      <Box ref={triggerRef}>
        <TextField
          size="small"
          label="Y Axis"
          value={displayValue}
          onClick={onToggle}
          inputProps={{ readOnly: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  sx={{
                    display: 'flex',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  <ArrowDropDown sx={{ color: 'action.active', fontSize: '1.5rem' }} />
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 120, cursor: 'pointer', '& *': { cursor: 'pointer' } }}
        />
      </Box>
      <Popover
        open={isOpen}
        anchorEl={triggerRef.current}
        onClose={() => {
          if (rangeError) onChange({ yAxisFrom: 0, yAxisTo: 100 });
          onToggle();
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 200 }}>
          <FormControlLabel
            control={
              <Switch
                checked={yAxisAuto}
                onChange={(e) =>
                  onChange({ yAxisAuto: e.target.checked, yAxisFrom: 0, yAxisTo: 100 })
                }
              />
            }
            label="Auto Y axis"
          />
          <TextField
            size="small"
            label="Min"
            type="number"
            value={yAxisFrom ?? ''}
            disabled={yAxisAuto}
            error={rangeError}
            onChange={(e) =>
              onChange({ yAxisFrom: e.target.value !== '' ? Number(e.target.value) : null })
            }
          />
          <TextField
            size="small"
            label="Max"
            type="number"
            value={yAxisTo ?? ''}
            disabled={yAxisAuto}
            error={rangeError}
            helperText={rangeError ? 'Min value must be less than Max value' : undefined}
            onChange={(e) =>
              onChange({ yAxisTo: e.target.value !== '' ? Number(e.target.value) : null })
            }
          />
        </Box>
      </Popover>
    </Box>
  );
};
